import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Button,
    IconButton,
    Typography,
    Box,
    Avatar,
    Grid,
    Collapse,
    Alert,
    CircularProgress
} from '@mui/material';
import { PiIdentificationCardLight as NameCardIcon } from "react-icons/pi";
import {
    IoClose as Close,
    IoPersonOutline as PersonIcon,
    IoCallOutline as ContactIcon,
    IoLocationOutline as LocationIcon,
    IoBriefcaseOutline as DeptIcon,
    IoDocumentTextOutline as RemarksIcon,
} from 'react-icons/io5';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { getCountries, getStates } from 'country-state-picker';
import PhoneInput, { isValidPhoneNumber, isPossiblePhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ErrorHandler from '@Root/ErrorHandler';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import OrganizationCardService from '@Helpers/URLHelpers/WeboutService/OrganizationCardService';
import OrganizationCardForm from '@RegistriesForms/OrganizationCardForm';
import 'react-phone-number-input/style.css';
import './NameCardForm.scss';

export default function NameCardForm(props) {
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [designation, setDesignation] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [areaCode, setAreaCode] = useState('');
    const [organization, setOrganization] = useState(null);
    const [organizationCardList, setOrganizationCardList] = useState([]);
    const [photo, setPhoto] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [uploadStatus, setUploadStatus] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [department, setDepartment] = useState('');
    const [branch, setBranch] = useState('');
    const [address, setAddress] = useState('');
    const [pinCode, setPinCode] = useState('');
    const [userState, setUserState] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [states, setStates] = useState([]);
    const [userCountry, setUserCountry] = useState(null);
    const [country, setCountry] = useState('');
    const [remarks, setRemarks] = useState('');
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isPossibleNumber, setIsPossibleNumber] = useState(false);
    const [isValidNumber, setIsValidNumber] = useState(false);
    const [showOrganizationCardForm, setShowOrganizationCardForm] = useState(false);

    const countries = getCountries();
    const errorHandler = new ErrorHandler();
    const nameCardService = new NameCardService();
    const organizationCardService = new OrganizationCardService();
    const { currentProjectId } = useReduxProject();
    const className = 'nameCardForm';

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        try {
            let organizationCardListObj = await organizationCardService.getListByProject(currentProjectId);
            organizationCardListObj = JSON.parse(organizationCardListObj.body);
            setOrganizationCardList(organizationCardListObj || []);
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    // Email is optional, so a blank value is valid — this guards handleSubmit's
    // isValidEmail check, not just the format of whatever the agent chooses to type.
    const emailValidCheck = (email) => {
        const validRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
        return !email || !!email.match(validRegex);
    };

    const handleNameChange = (e) => {
        setName(DOMPurify.sanitize(e.target.value));
    };

    const handleEmailChange = (e) => {
        const sanitized = DOMPurify.sanitize(e.target.value);
        setEmail(sanitized);
        setIsValidEmail(emailValidCheck(sanitized));
    };

    const handleDesignationChange = (e) => {
        setDesignation(DOMPurify.sanitize(e.target.value));
    };

    const handleContactNumberChange = (newNumber) => {
        setContactNumber(newNumber);
        setIsPossibleNumber(newNumber && isPossiblePhoneNumber(newNumber));
        setIsValidNumber(newNumber && isValidPhoneNumber(newNumber));
        if (newNumber) {
            try {
                const parsed = parsePhoneNumber(newNumber);
                setAreaCode(parsed ? `+${parsed.countryCallingCode}` : '');
            } catch {
                setAreaCode('');
            }
        } else {
            setAreaCode('');
        }
    };

    const handleOrganizationChange = (e, value) => {
        setOrganization(value);
    };

    const handleDepartmentChange = (e) => {
        setDepartment(DOMPurify.sanitize(e.target.value));
    };

    const handleBranchChange = (e) => {
        setBranch(DOMPurify.sanitize(e.target.value));
    };

    const handleAddressChange = (e) => {
        setAddress(DOMPurify.sanitize(e.target.value));
    };

    const handlePinCodeChange = (e) => {
        const re = /^[0-9\b]+$/;
        if (e.target.value === '' || re.test(e.target.value)) {
            setPinCode(e.target.value);
        }
    };

    const handleCountryChange = (e, value) => {
        if (value) {
            setStates(getStates(value.code));
            setCountry(value.name);
            setCountryCode(value.code);
            setUserCountry(value);
            setUserState('');
        }
    };

    const handleStateChange = (e, value) => {
        setUserState(value || '');
    };

    const handleRemarksChange = (e) => {
        setRemarks(DOMPurify.sanitize(e.target.value));
    };

    const handleFileChange = async (e) => {
        e?.preventDefault();
        const file = e.target.files[0];
        if (file) {
            if (validateImage(file)) {
                const base64File = await convertFileToBase64(file);
                setPhoto(base64File);
                setImageUrl(URL.createObjectURL(file));
                setUploadStatus('');
            }
        }
    };

    const validateImage = (file) => {
        if (file.type.split('/')[0] === 'image') {
            const sizeLimit = 1024 * 1024;
            if (file.size > sizeLimit) {
                setUploadStatus('Image size exceeded 1MB!');
                return false;
            }
            return true;
        } else {
            setUploadStatus('Upload image file!');
            return false;
        }
    };

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const toggleShowOrganizationCardForm = () => {
        setShowOrganizationCardForm(!showOrganizationCardForm);
    };

    const handleSubmit = async (e) => {
        try {
            e?.preventDefault();
            if (!isValidEmail || (contactNumber && (!isPossibleNumber || !isValidNumber))) {
                return;
            }
            setLoading(true);
            const parsedPhone = contactNumber ? parsePhoneNumber(contactNumber) : null;
            const nameCardObj = {
                projectId: currentProjectId,
                name,
                email,
                organization: organization?.id || 0,
                designation,
                contactNumber: parsedPhone ? parsedPhone.nationalNumber : contactNumber,
                areaCode,
                photo,
                department: showAdvanced ? department : undefined,
                branch: showAdvanced ? branch : undefined,
                address: showAdvanced ? address : undefined,
                state: showAdvanced ? userState : undefined,
                country: showAdvanced ? country : undefined,
                countryCode: showAdvanced ? countryCode : undefined,
                pinCode: showAdvanced ? pinCode : undefined,
                remarks: showAdvanced ? remarks : undefined
            };
            await nameCardService.createNameCard(nameCardObj);
            await props.reloadData();
            props.closePopup();
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const fieldSx = {
        borderRadius: '8px',
        backgroundColor: 'var(--color-muiBackground)',
        color: 'var(--color-text)',
    };

    return (
        <>
            <Dialog
                open={props.open}
                onClose={props.closePopup}
                maxWidth="md"
                fullWidth
                className={className}
                PaperProps={{
                    className: `${className}__paper`,
                    sx: { background: 'var(--color-componentBackground)' }
                }}
            >
                <DialogTitle className={`${className}__header`}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <NameCardIcon className={`${className}__header__icon`} />
                            <Typography variant="h6" className={`${className}__header__title`}>
                                Create Name Card
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={props.closePopup}
                            size="small"
                            sx={{ color: 'var(--color-primaryGrey)', '&:hover': { background: 'var(--color-hoverBackground)' } }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent className={`${className}__content`}>
                    <form onSubmit={handleSubmit} id="name-card-form">
                        {/* ── Basic Info ── */}
                        <Box className={`${className}__section`}>
                            <div className={`${className}__section-head`}>
                                <PersonIcon />
                                <span>Basic Information</span>
                            </div>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <TextField
                                            label="Full Name"
                                            placeholder="Enter full name"
                                            value={name}
                                            onChange={handleNameChange}
                                            required
                                            fullWidth
                                            inputProps={{ maxLength: 200 }}
                                            InputProps={{ sx: fieldSx }}
                                            helperText={name.length >= 180 ? `${name.length}/200 characters` : ''}
                                            FormHelperTextProps={{
                                                className: name.length >= 200 ? `${className}__charLimit` : ''
                                            }}
                                        />
                                        <TextField
                                            label="Email"
                                            placeholder="Enter email address"
                                            type="email"
                                            value={email}
                                            onChange={handleEmailChange}
                                            fullWidth
                                            inputProps={{ maxLength: 200 }}
                                            InputProps={{ sx: fieldSx }}
                                            error={!!(email && !isValidEmail)}
                                            helperText={email && !isValidEmail ? 'Please enter a valid email' : ''}
                                        />
                                        <TextField
                                            label="Designation"
                                            placeholder="e.g. Senior Engineer"
                                            value={designation}
                                            onChange={handleDesignationChange}
                                            fullWidth
                                            inputProps={{ maxLength: 200 }}
                                            InputProps={{ sx: fieldSx }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box className={`${className}__photoSection`}>
                                        <input
                                            type="file"
                                            accept="image/jpeg, image/png, image/webp"
                                            id="fileInputNameCard"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                        />
                                        <label htmlFor="fileInputNameCard">
                                            <Box className={`${className}__photoContainer`}>
                                                <Avatar
                                                    className={`${className}__avatar`}
                                                    src={imageUrl}
                                                >
                                                    <NameCardIcon className={`${className}__avatarIcon`} />
                                                </Avatar>
                                                <div className={`${className}__photoOverlay`}>
                                                    <PhotoCameraIcon />
                                                </div>
                                                <IconButton
                                                    component="span"
                                                    className={`${className}__photoBtn`}
                                                >
                                                    <PhotoCameraIcon />
                                                </IconButton>
                                            </Box>
                                        </label>
                                        <Typography
                                            variant="caption"
                                            align="center"
                                            sx={{ color: 'var(--color-primaryGrey)' }}
                                        >
                                            Click to upload photo
                                        </Typography>
                                        {uploadStatus && (
                                            <Alert severity="error" className={`${className}__uploadError`}>
                                                {uploadStatus}
                                            </Alert>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* ── Contact & Organization ── */}
                        <Box className={`${className}__section`}>
                            <div className={`${className}__section-head`}>
                                <ContactIcon />
                                <span>Contact & Organization</span>
                            </div>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Box className={`${className}__phoneInput`}>
                                        <Typography variant="caption" className={`${className}__phoneLabel`}>
                                            Contact Number
                                        </Typography>
                                        <PhoneInput
                                            international
                                            defaultCountry="IN"
                                            value={contactNumber}
                                            onChange={handleContactNumberChange}
                                            className={`${className}__phoneField`}
                                        />
                                        {contactNumber && (!isPossibleNumber || !isValidNumber) && (
                                            <Typography variant="caption" color="error">
                                                Invalid phone number
                                            </Typography>
                                        )}
                                    </Box>
                                    {areaCode && (
                                        <Typography variant="caption" sx={{ mt: 1, color: 'var(--color-primaryGrey)' }}>
                                            Area Code: <strong>{areaCode}</strong>
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box display="flex" flexDirection="column" gap={1}>
                                        <Autocomplete
                                            options={organizationCardList}
                                            value={organization}
                                            onChange={handleOrganizationChange}
                                            getOptionLabel={(option) => option.name}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Organization"
                                                    placeholder="Select organization"
                                                    InputProps={{ ...params.InputProps, sx: fieldSx }}
                                                />
                                            )}
                                        />
                                        <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<AddCircleOutlineIcon />}
                                            onClick={toggleShowOrganizationCardForm}
                                            className={`${className}__createOrgBtn`}
                                        >
                                            Create Organization Card
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Advanced Details Toggle */}
                        <Box className={`${className}__toggleSection`}>
                            <Button
                                variant="text"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                className={`${className}__toggleBtn`}
                            >
                                {showAdvanced ? 'Hide' : 'Show'} Additional Details
                            </Button>
                        </Box>

                        {/* ── Advanced Section ── */}
                        <Collapse in={showAdvanced}>
                            <Box className={`${className}__section`}>
                                <div className={`${className}__section-head`}>
                                    <DeptIcon />
                                    <span>Department & Branch</span>
                                </div>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Department"
                                            placeholder="Enter department"
                                            value={department}
                                            onChange={handleDepartmentChange}
                                            fullWidth
                                            inputProps={{ maxLength: 200 }}
                                            InputProps={{ sx: fieldSx }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Branch"
                                            placeholder="Enter branch"
                                            value={branch}
                                            onChange={handleBranchChange}
                                            fullWidth
                                            inputProps={{ maxLength: 200 }}
                                            InputProps={{ sx: fieldSx }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box className={`${className}__section`}>
                                <div className={`${className}__section-head`}>
                                    <LocationIcon />
                                    <span>Location</span>
                                </div>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Address"
                                            placeholder="Enter address"
                                            value={address}
                                            onChange={handleAddressChange}
                                            multiline
                                            rows={4}
                                            fullWidth
                                            InputProps={{ sx: fieldSx }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box display="flex" flexDirection="column" gap={2}>
                                            <TextField
                                                label="Pin Code"
                                                placeholder="Enter pin code"
                                                value={pinCode}
                                                onChange={handlePinCodeChange}
                                                fullWidth
                                                inputProps={{ maxLength: 20 }}
                                                InputProps={{ sx: fieldSx }}
                                            />
                                            <Autocomplete
                                                options={countries}
                                                value={userCountry}
                                                onChange={handleCountryChange}
                                                getOptionLabel={(option) => option.name}
                                                isOptionEqualToValue={(option, value) => option.code === value.code}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Country" placeholder="Select country"
                                                        InputProps={{ ...params.InputProps, sx: fieldSx }}
                                                    />
                                                )}
                                            />
                                            <Autocomplete
                                                options={states}
                                                value={userState}
                                                onChange={handleStateChange}
                                                disabled={!userCountry}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="State" placeholder="Select state"
                                                        InputProps={{ ...params.InputProps, sx: fieldSx }}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box className={`${className}__section`}>
                                <div className={`${className}__section-head`}>
                                    <RemarksIcon />
                                    <span>Additional Notes</span>
                                </div>
                                <TextField
                                    label="Remarks"
                                    placeholder="Enter any additional remarks"
                                    value={remarks}
                                    onChange={handleRemarksChange}
                                    multiline
                                    rows={4}
                                    fullWidth
                                    InputProps={{ sx: { ...fieldSx, alignItems: 'flex-start' } }}
                                />
                            </Box>
                        </Collapse>
                    </form>
                </DialogContent>

                <DialogActions className={`${className}__footer`}>
                    <Button
                        onClick={props.closePopup}
                        disabled={loading}
                        variant="outlined"
                        sx={{
                            color: 'var(--color-text)',
                            borderColor: 'var(--color-darkBorder)',
                            '&:hover': { borderColor: 'var(--color-primaryBlue)', background: 'var(--color-hoverBackground)' }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="name-card-form"
                        variant="contained"
                        disabled={loading || !isValidEmail || (contactNumber && (!isPossibleNumber || !isValidNumber))}
                        startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                        sx={{
                            background: 'var(--color-primaryBlue)',
                            '&:hover': { background: 'var(--color-darkBlue)' },
                            '&.Mui-disabled': { background: 'var(--color-disabled)', color: '#fff' }
                        }}
                    >
                        {loading ? 'Creating…' : 'Create Name Card'}
                    </Button>
                </DialogActions>
            </Dialog>

            {showOrganizationCardForm && (
                <OrganizationCardForm
                    open={showOrganizationCardForm}
                    closePopup={toggleShowOrganizationCardForm}
                    reloadData={renderData}
                />
            )}
        </>
    );
}
